import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SuperAdminClinicsPage.css";

/**
 * Super Admin — Clinics
 *
 * Production scope:
 * - Fetch + display clinic list from GET /api/superadmin-clinics-list
 * - Client-side filters: Search + City + Region + Status + governance signal
 * - Show governance status, public listing completeness, and Clinic Admin ownership
 *
 * Notes:
 * - This page is intentionally clinic/governance only.
 * - No patient PHI is shown here.
 */

type ClinicRow = {
  ClinicID: number | string;
  ClinicName: string | null;
  CityID?: number | null;
  CityName: string | null;
  RegionID?: number | null;
  RegionName: string | null;
  GovernanceStatus: string | null;
  UpdatedAt: string | null;
  CreatedAt: string | null;
  Province?: string | null;

  // Optional public-listing fields.
  AddressLine1?: string | null;
  PostalCode?: string | null;
  Phone?: string | null;
  Website?: string | null;
  Latitude?: number | string | null;
  Longitude?: number | string | null;
  ApprovedAt?: string | null;
  ApprovedByUserID?: number | string | null;
  ActiveClinicAdminCount?: number | string | null;

  // Allow old/new API shapes without breaking the page.
  [key: string]: unknown;
};

type ClinicsListResponse = {
  clinics?: ClinicRow[];
};

type ApiErrorShape = { error?: string; fields?: Record<string, string> } | null;

type BadgeTone = "success" | "danger" | "warning" | "neutral" | "info";

type PublicEligibilityResult = {
  known: boolean;
  eligible: boolean;
  missing: string[];
};

export default function SuperAdminClinicsPage() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<ClinicRow[]>([]);

  // Filters — client-side v1.
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState("");
  const [governanceSignal, setGovernanceSignal] = useState("");

  async function loadClinics() {
    setLoading(true);
    setErr(null);

    try {
      const res = await fetch("/api/superadmin-clinics-list", {
        credentials: "include",
      });

      if (!res.ok) {
        let msg = `Failed to load clinics (${res.status})`;
        try {
          const j = (await res.json()) as ApiErrorShape;
          if (j?.error) msg = j.error;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      const data = (await res.json()) as ClinicsListResponse;
      const list = Array.isArray(data?.clinics) ? data.clinics : [];

      setRows(list);
    } catch (e: any) {
      setErr(e?.message || "Failed to load clinics");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setErr(null);

      try {
        const res = await fetch("/api/superadmin-clinics-list", {
          credentials: "include",
        });

        if (!res.ok) {
          let msg = `Failed to load clinics (${res.status})`;
          try {
            const j = (await res.json()) as ApiErrorShape;
            if (j?.error) msg = j.error;
          } catch {
            // ignore
          }
          throw new Error(msg);
        }

        const data = (await res.json()) as ClinicsListResponse;
        const list = Array.isArray(data?.clinics) ? data.clinics : [];

        if (!cancelled) setRows(list);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Failed to load clinics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const cityOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) {
      const v = String(r.CityName || "").trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const regionOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) {
      const v = String(r.RegionName || "").trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const statusOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) {
      const v = String(r.GovernanceStatus || "").trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const summary = useMemo(() => {
    let approved = 0;
    let suspended = 0;
    let publicIssue = 0;
    let publicUnknown = 0;
    let withoutAdmin = 0;
    let adminUnknown = 0;

    for (const row of rows) {
      const statusNorm = norm(row.GovernanceStatus);
      const eligibility = getPublicEligibility(row);
      const adminCount = getActiveAdminCount(row);

      if (statusNorm === "approved") approved += 1;
      if (statusNorm === "suspended" || statusNorm === "deactivated") {
        suspended += 1;
      }

      if (statusNorm === "approved") {
        if (!eligibility.known) publicUnknown += 1;
        else if (!eligibility.eligible) publicIssue += 1;

        if (adminCount === null) adminUnknown += 1;
        else if (adminCount <= 0) withoutAdmin += 1;
      }
    }

    return {
      total: rows.length,
      approved,
      suspended,
      publicIssue,
      publicUnknown,
      withoutAdmin,
      adminUnknown,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    const cn = city.trim().toLowerCase();
    const rn = region.trim().toLowerCase();
    const sn = status.trim().toLowerCase();
    const signal = governanceSignal.trim().toLowerCase();

    return rows.filter((r) => {
      const name = String(r.ClinicName || "").toLowerCase();
      const id = String(r.ClinicID || "").toLowerCase();
      const c = String(r.CityName || "").toLowerCase();
      const reg = String(r.RegionName || "").toLowerCase();
      const province = String(r.Province || "").toLowerCase();
      const st = String(r.GovernanceStatus || "").toLowerCase();

      const eligibility = getPublicEligibility(r);
      const adminCount = getActiveAdminCount(r);
      const approved = st === "approved";

      if (qn) {
        const hay = `${id} ${name} ${c} ${reg} ${province}`.trim();
        if (!hay.includes(qn)) return false;
      }
      if (cn && c !== cn) return false;
      if (rn && reg !== rn) return false;
      if (sn && st !== sn) return false;

      if (signal === "public-issue") {
        if (!approved || !eligibility.known || eligibility.eligible) return false;
      }

      if (signal === "public-unknown") {
        if (!approved || eligibility.known) return false;
      }

      if (signal === "without-admin") {
        if (!approved || adminCount === null || adminCount > 0) return false;
      }

      if (signal === "admin-unknown") {
        if (!approved || adminCount !== null) return false;
      }

      return true;
    });
  }, [rows, q, city, region, status, governanceSignal]);

  const openClinic = (clinicId: number | string) => {
    nav(`/portal/super-admin/clinics/${encodeURIComponent(String(clinicId))}`);
  };

  const goCreateBasicClinic = () => {
    nav("/portal/super-admin/clinics/create-basic");
  };

  return (
    <div className="sa-clinics">
      {/* Header */}
      <div className="sa-clinics__header">
        <div className="sa-clinics__titleWrap">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 10px",
              borderRadius: 999,
              background: "#eaf4ff",
              border: "1px solid #cfe6ff",
              color: "#1669b3",
              fontSize: 12,
              fontWeight: 650,
              marginBottom: 10,
            }}
          >
            Clinic governance
          </div>

          <h1 className="sa-clinics__title">Clinics</h1>

          <p className="sa-clinics__subtitle">
            View system-wide clinic records, governance status, public listing
            completeness, and Clinic Admin ownership. No patient PHI is shown here.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={loadClinics}
            disabled={loading}
            style={{
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #d1d9e6",
              background: "#ffffff",
              color: "#102a43",
              fontWeight: 650,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.65 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>

          <button
            type="button"
            onClick={goCreateBasicClinic}
            className="sa-clinics__createBtn"
          >
            + Create basic clinic
          </button>
        </div>
      </div>

      {/* Summary */}
      <section
        style={{
          padding: 16,
          borderRadius: 18,
          border:
            summary.publicIssue > 0 || summary.withoutAdmin > 0
              ? "1px solid #f4d18f"
              : "1px solid #b9dcff",
          background:
            summary.publicIssue > 0 || summary.withoutAdmin > 0
              ? "#fffaf0"
              : "#f7fbff",
          boxShadow: "0 8px 24px rgba(15, 80, 135, 0.04)",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <PillBadge label={`${summary.total} clinics`} tone="info" />
          <PillBadge label={`${summary.approved} approved`} tone="success" />
          <PillBadge
            label={`${summary.suspended} suspended`}
            tone={summary.suspended > 0 ? "warning" : "neutral"}
          />
          <PillBadge
            label={`${summary.publicIssue} public listing issue${
              summary.publicIssue === 1 ? "" : "s"
            }`}
            tone={summary.publicIssue > 0 ? "warning" : "neutral"}
          />
          <PillBadge
            label={`${summary.withoutAdmin} without admin`}
            tone={summary.withoutAdmin > 0 ? "warning" : "neutral"}
          />
          <PillBadge label="No patient PHI" tone="success" />
        </div>

        {summary.publicUnknown > 0 || summary.adminUnknown > 0 ? (
          <div
            style={{
              marginTop: 10,
              color: "#6b7a94",
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 450,
            }}
          >
            Some governance checks are marked “not returned” when the clinic list API
            does not include public eligibility fields or active Clinic Admin counts.
          </div>
        ) : null}
      </section>

      {/* Filters */}
      <div className="sa-clinics__filters">
        <Field label="Search">
          <input
            type="text"
            placeholder="Clinic name, ID, city, region…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="sa-clinics__input"
          />
        </Field>

        <Field label="City">
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="sa-clinics__input"
          >
            <option value="">All</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Region">
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="sa-clinics__input"
          >
            <option value="">All</option>
            {regionOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="sa-clinics__input"
          >
            <option value="">All</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Governance signal">
          <select
            value={governanceSignal}
            onChange={(e) => setGovernanceSignal(e.target.value)}
            className="sa-clinics__input"
          >
            <option value="">All</option>
            <option value="public-issue">Public listing issue</option>
            <option value="public-unknown">Public eligibility not returned</option>
            <option value="without-admin">Approved clinic without admin</option>
            <option value="admin-unknown">Admin count not returned</option>
          </select>
        </Field>
      </div>

      {/* List */}
      <div
        className={`sa-clinics__card ${err ? "sa-clinics__card--error" : ""}`}
      >
        <div className="sa-clinics__cardHeader">
          <div>
            <div className="sa-clinics__cardTitle">Clinic list</div>
            <div
              style={{
                marginTop: 4,
                color: "#6b7a94",
                fontSize: 13,
                fontWeight: 450,
              }}
            >
              Public listing completeness and admin ownership are governance safety checks.
            </div>
          </div>

          <div className="sa-clinics__count">
            {loading ? "Loading…" : `${filtered.length} of ${rows.length}`}
          </div>
        </div>

        {/* Error */}
        {err ? <div className="sa-clinics__error">{err}</div> : null}

        {/* Table */}
        <div className="sa-clinics__tableWrap">
          <table className="sa-clinics__table">
            <thead>
              <tr>
                <Th>Clinic</Th>
                <Th>Location</Th>
                <Th>Status</Th>
                <Th>Public Listing</Th>
                <Th>Clinic Admin</Th>
                <Th>Updated</Th>
                <Th>&nbsp;</Th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr className="sa-clinics__tr">
                  <td
                    className="sa-clinics__td sa-clinics__td--muted"
                    colSpan={7}
                  >
                    Loading clinics…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr className="sa-clinics__tr">
                  <td
                    className="sa-clinics__td sa-clinics__td--muted"
                    colSpan={7}
                  >
                    No clinics found.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const eligibility = getPublicEligibility(r);
                  const adminCount = getActiveAdminCount(r);
                  const statusNorm = norm(r.GovernanceStatus);
                  const approved = statusNorm === "approved";

                  return (
                    <tr key={String(r.ClinicID)} className="sa-clinics__tr">
                      <td className="sa-clinics__td">
                        <div className="sa-clinics__clinicName">
                          {r.ClinicName || "—"}
                        </div>

                        <div className="sa-clinics__clinicMeta">
                          ClinicID: {String(r.ClinicID)}
                        </div>

                        {r.ApprovedAt ? (
                          <div className="sa-clinics__clinicMeta">
                            Approved: {fmtDate(r.ApprovedAt)}
                          </div>
                        ) : null}
                      </td>

                      <td className="sa-clinics__td">
                        <div style={{ fontWeight: 650, color: "#102a43" }}>
                          {r.CityName || "—"}
                        </div>

                        <div className="sa-clinics__clinicMeta">
                          {r.RegionName || "—"}
                          {r.Province ? ` • ${r.Province}` : ""}
                        </div>

                        {hasAnyAddressField(r) ? (
                          <div
                            className="sa-clinics__clinicMeta"
                            style={{ marginTop: 4 }}
                          >
                            {formatAddressMeta(r)}
                          </div>
                        ) : null}
                      </td>

                      <td className="sa-clinics__td">
                        <StatusPill value={r.GovernanceStatus} />
                      </td>

                      <td className="sa-clinics__td">
                        <PublicListingPill eligibility={eligibility} />

                        {approved &&
                        eligibility.known &&
                        !eligibility.eligible ? (
                          <div
                            style={{
                              marginTop: 6,
                              color: "#92400e",
                              fontSize: 12,
                              fontWeight: 650,
                              lineHeight: 1.4,
                            }}
                          >
                            Missing: {eligibility.missing.join(", ")}
                          </div>
                        ) : null}
                      </td>

                      <td className="sa-clinics__td">
                        <AdminOwnershipPill
                          approved={approved}
                          adminCount={adminCount}
                        />
                      </td>

                      <td className="sa-clinics__td">
                        {fmtDate(r.UpdatedAt || r.CreatedAt)}
                      </td>

                      <td className="sa-clinics__td sa-clinics__td--right">
                        <button
                          type="button"
                          onClick={() => openClinic(r.ClinicID)}
                          className="sa-clinics__rowBtn"
                        >
                          View/Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="sa-clinics__hint">
          Approved clinics should have an active Clinic Admin and complete public
          listing requirements before appearing in public search.
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sa-clinics__field">
      <div className="sa-clinics__label">{label}</div>
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="sa-clinics__th">{children}</th>;
}

function StatusPill({ value }: { value: string | null }) {
  const v = String(value || "").trim();
  const lower = v.toLowerCase();

  let cls = "sa-clinics__pill";
  if (!v) cls += " sa-clinics__pill--empty";
  else if (lower === "approved") cls += " sa-clinics__pill--approved";
  else if (lower === "suspended" || lower === "deactivated")
    cls += " sa-clinics__pill--suspended";
  else cls += " sa-clinics__pill--other";

  return <span className={cls}>{v || "—"}</span>;
}

function PillBadge({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: BadgeTone;
}) {
  const styles: Record<
    BadgeTone,
    { bg: string; bd: string; fg: string; dot: string }
  > = {
    success: {
      bg: "rgba(22, 163, 74, 0.10)",
      bd: "rgba(22, 163, 74, 0.25)",
      fg: "#166534",
      dot: "#16a34a",
    },
    danger: {
      bg: "rgba(185, 28, 28, 0.08)",
      bd: "rgba(185, 28, 28, 0.22)",
      fg: "#991b1b",
      dot: "#dc2626",
    },
    warning: {
      bg: "#fffaf0",
      bd: "#f4d18f",
      fg: "#92400e",
      dot: "#b45309",
    },
    neutral: {
      bg: "#f8fafc",
      bd: "#e2e8f0",
      fg: "#64748b",
      dot: "#94a3b8",
    },
    info: {
      bg: "#eaf4ff",
      bd: "#cfe6ff",
      fg: "#1669b3",
      dot: "#1669b3",
    },
  };

  const s = styles[tone];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${s.bd}`,
        background: s.bg,
        color: s.fg,
        fontSize: 12,
        fontWeight: 750,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
      title={label}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: s.dot,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function PublicListingPill({
  eligibility,
}: {
  eligibility: PublicEligibilityResult;
}) {
  if (!eligibility.known) {
    return <PillBadge label="Not returned" tone="neutral" />;
  }

  if (eligibility.eligible) {
    return <PillBadge label="Ready" tone="success" />;
  }

  return <PillBadge label="Needs repair" tone="warning" />;
}

function AdminOwnershipPill({
  approved,
  adminCount,
}: {
  approved: boolean;
  adminCount: number | null;
}) {
  if (!approved) {
    return <PillBadge label="Not applicable" tone="neutral" />;
  }

  if (adminCount === null) {
    return <PillBadge label="Not returned" tone="neutral" />;
  }

  if (adminCount <= 0) {
    return <PillBadge label="No active admin" tone="warning" />;
  }

  return (
    <PillBadge
      label={`${adminCount} active admin${adminCount === 1 ? "" : "s"}`}
      tone="success"
    />
  );
}

function fmtDate(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function norm(value: unknown) {
  return String(value || "").trim().toLowerCase();
}

function hasValue(value: unknown) {
  return String(value ?? "").trim().length > 0;
}

function asNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getFirstValue(row: ClinicRow, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

function getActiveAdminCount(row: ClinicRow): number | null {
  return asNumberOrNull(
    getFirstValue(row, [
      "ActiveClinicAdminCount",
      "activeClinicAdminCount",
      "ClinicAdminCount",
      "clinicAdminCount",
      "ActiveAdminCount",
      "activeAdminCount",
    ])
  );
}

function getPublicEligibility(row: ClinicRow): PublicEligibilityResult {
  const explicit = getFirstValue(row, [
    "IsPublicEligible",
    "isPublicEligible",
    "PublicEligible",
    "publicEligible",
  ]);

  if (explicit !== undefined) {
    const eligible = explicit === true || explicit === 1 || norm(explicit) === "true";
    return { known: true, eligible, missing: eligible ? [] : ["Public eligibility"] };
  }

  const hasAnyPublicField =
    "AddressLine1" in row ||
    "addressLine1" in row ||
    "PostalCode" in row ||
    "postalCode" in row ||
    "Phone" in row ||
    "phone" in row ||
    "Latitude" in row ||
    "latitude" in row ||
    "Longitude" in row ||
    "longitude" in row;

  if (!hasAnyPublicField) {
    return { known: false, eligible: false, missing: [] };
  }

  const missing: string[] = [];

  if (!hasValue(getFirstValue(row, ["AddressLine1", "addressLine1"]))) {
    missing.push("Address");
  }

  if (!hasValue(row.CityID) && !hasValue(row.CityName)) {
    missing.push("City");
  }

  if (!hasValue(getFirstValue(row, ["PostalCode", "postalCode"]))) {
    missing.push("Postal code");
  }

  if (!hasValue(getFirstValue(row, ["Phone", "phone"]))) {
    missing.push("Phone");
  }

  if (
    !hasValue(getFirstValue(row, ["Latitude", "latitude"])) ||
    !hasValue(getFirstValue(row, ["Longitude", "longitude"]))
  ) {
    missing.push("Coordinates");
  }

  return {
    known: true,
    eligible: missing.length === 0,
    missing,
  };
}

function hasAnyAddressField(row: ClinicRow) {
  return Boolean(
    hasValue(getFirstValue(row, ["AddressLine1", "addressLine1"])) ||
      hasValue(getFirstValue(row, ["PostalCode", "postalCode"])) ||
      hasValue(getFirstValue(row, ["Phone", "phone"]))
  );
}

function formatAddressMeta(row: ClinicRow) {
  const parts = [
    String(getFirstValue(row, ["AddressLine1", "addressLine1"]) || "").trim(),
    String(getFirstValue(row, ["PostalCode", "postalCode"]) || "").trim(),
    String(getFirstValue(row, ["Phone", "phone"]) || "").trim(),
  ].filter(Boolean);

  return parts.length ? parts.join(" • ") : "—";
}